import { formatNumber, formatTtcPrice } from "./helpers";
import {
    getVehicleCardDefaultImageWidthForLayout,
    getVehicleCardImageSizesForLayout,
    getVehicleCardImageWidthsForLayout,
    getVehicleImageSrcSet,
    getVehicleImageUrl,
} from "@/helpers/imageKit";
import { generateVehicleImageAlt } from "@/helpers/vehicle";
import type { VehicleAlgoliaRecord } from "@/types";
import {
    appendPhotoSourceSpans,
    getDisplayPhotos,
    setupAllVehicleCardCarousels,
} from "@/shared/vehicle-card-carousel.utils";
import { bindCompareFavoriteButtons } from "./compare-favorite";

const MARKETPLACE_CARD_IMAGE_LAYOUT = "default";

function vehicleImageContextFromHit(
    hit: Record<string, unknown>,
    photos: string[],
): VehicleAlgoliaRecord {
    const useLocal =
        Array.isArray(hit.local_photos) && hit.local_photos.length > 0;

    return {
        ...hit,
        local_photos: useLocal ? photos : [],
        photos: useLocal ? [] : photos,
    } as VehicleAlgoliaRecord;
}

const ENERGY_LABELS: Record<string, string> = {
    diesel: "Diesel",
    electric: "Électrique",
    essence: "Essence",
    hybrid: "Hybride",
};

function registrationYear(hit: Record<string, unknown>): string | null {
    const fromField = hit.registration_year;
    if (fromField !== null && fromField !== undefined && fromField !== "") {
        return String(fromField);
    }

    const regAt = hit.registration_at;
    if (regAt === null || regAt === undefined || regAt === "") {
        return null;
    }

    if (typeof regAt === "number" && regAt > 1900 && regAt < 2100) {
        return String(regAt);
    }

    const regString = String(regAt);
    const match = regString.match(/^\d{4}/);

    return match ? match[0] : regString;
}

function energyLabel(energyType: unknown): string | null {
    if (energyType === null || energyType === undefined || energyType === "") {
        return null;
    }

    const key = String(energyType).toLowerCase();

    return ENERGY_LABELS[key] ?? String(energyType);
}

function makeLine(hit: Record<string, unknown>): string {
    const make =
        hit.make !== null && hit.make !== undefined
            ? String(hit.make).trim()
            : "";

    return make || "—";
}

// Même règle que VehicleCardHelper::modelLine (rendu serveur) : la finition reprend souvent le modèle
// (« Huracán » + « Huracán EVO STO »), on ne le répète pas
function modelParts(hit: Record<string, unknown>): string[] {
    const model = hit.model != null ? String(hit.model).trim() : "";
    const specification =
        hit.specification != null ? String(hit.specification).trim() : "";
    const repeats =
        model !== "" &&
        specification.toLowerCase().startsWith(model.toLowerCase());

    return [repeats ? "" : model, specification].filter((part) => part !== "");
}

function modelLine(hit: Record<string, unknown>): string {
    const line = modelParts(hit).join(" ");

    return line !== "" ? line : "—";
}

function powerCh(hit: Record<string, unknown>): number | null {
    for (const field of ["power", "power_hp"] as const) {
        const value = hit[field];
        if (value === null || value === undefined || value === "") {
            continue;
        }

        const hp = Number(value);
        if (Number.isFinite(hp) && hp > 0) {
            return hp;
        }
    }

    return null;
}

function specsParts(hit: Record<string, unknown>): string[] {
    const parts: string[] = [];

    const mileage = hit.mileage;
    if (mileage !== null && mileage !== undefined && mileage !== "") {
        parts.push(`${formatNumber(Number(mileage))} km`);
    }

    const hp = powerCh(hit);
    if (hp !== null) {
        parts.push(`${formatNumber(hp)} ch`);
    }

    const energy = energyLabel(hit.energy_type);
    if (energy) {
        parts.push(energy);
    }

    return parts;
}

function metaLine(hit: Record<string, unknown>): string {
    const parts: string[] = [];
    const year = registrationYear(hit);
    if (year) {
        parts.push(year);
    }
    parts.push(...specsParts(hit));

    return parts.length > 0 ? parts.join("  ·  ") : "—";
}

function titleLine(hit: Record<string, unknown>): string {
    const make = hit.make != null ? String(hit.make).trim() : "";

    return [make, ...modelParts(hit)].filter((part) => part !== "").join(" ");
}

/** Typesense may return 0 or omit the field — only treat real positive prices as present. */
function hasPositivePrice(value: unknown): boolean {
    if (value === null || value === undefined || value === "") {
        return false;
    }

    const amount = Number(value);

    return Number.isFinite(amount) && amount > 0;
}

function setBlockVisible(
    element: Element | null | undefined,
    visible: boolean,
): void {
    if (!element) return;
    element.classList.toggle("vehicle-card__block--hidden", !visible);
}

/**
 * Clones the Blade-rendered <template id="vehicle-card-tpl"> and hydrates
 * it with hit data, so the HTML structure is defined once in the Blade
 * partial (partials/vehicle-card.blade.php) — no duplication.
 */
export function vehicleCardTemplate(hit: Record<string, any>): string {
    const tpl = document.getElementById(
        "vehicle-card-tpl",
    ) as HTMLTemplateElement | null;
    if (!tpl) return "";

    const clone = tpl.content.cloneNode(true) as DocumentFragment;
    const card = clone.querySelector(".vehicle-card") as HTMLElement;
    if (!card) return "";

    const photos: string[] = hit.local_photos ?? hit.photos ?? [];
    const photo = Array.isArray(photos) ? photos[0] ?? "" : "";
    const monthlyPrice = hit.monthly_price;
    const totalPrice = hit.price;
    const hasPromotion =
        Boolean(hit.has_promotion) && Number(hit.promotion_value) > 0;
    const promotionValue = hasPromotion ? Number(hit.promotion_value) : null;
    const priceBeforePromotion = hasPromotion
        ? hit.price_before_promotion
        : null;
    const slug: string = hit.slug ?? "";
    const id: string = hit.id ?? hit.objectID ?? "";
    const vehicleUrl = slug && id ? `/details/${slug}-${id}` : "#";
    const altText = titleLine(hit) || modelLine(hit);

    const brandEl = card.querySelector(".vehicle-card__brand");
    if (brandEl) {
        brandEl.textContent = makeLine(hit);
    }

    const modelLink = card.querySelector<HTMLAnchorElement>(
        ".vehicle-card__model a",
    );
    if (modelLink) {
        modelLink.textContent = modelLine(hit);
        modelLink.href = vehicleUrl;
    }

    const specsEl = card.querySelector(".vehicle-card__specs");
    if (specsEl) {
        specsEl.textContent = metaLine(hit);
    }

    const hasMonthly = hasPositivePrice(monthlyPrice);
    const hasTotal = hasPositivePrice(totalPrice);
    const monthlyBlock = card.querySelector(".vehicle-card__monthly");
    const monthlyValueEl = card.querySelector(".vehicle-card__monthly-value");
    if (hasMonthly && monthlyValueEl) {
        monthlyValueEl.textContent = `${formatNumber(Number(monthlyPrice))} €`;
    }
    setBlockVisible(monthlyBlock, hasMonthly);

    const totalBlock = card.querySelector(".vehicle-card__total");
    const priceValueEl = card.querySelector(".vehicle-card__price-value");
    const priceWasEl = card.querySelector(".vehicle-card__price-was");
    const dropchipEl = card.querySelector(".vehicle-card__dropchip");
    if (hasPromotion && promotionValue !== null && dropchipEl) {
        dropchipEl.textContent = `↓ −${formatNumber(promotionValue)} €`;
        setBlockVisible(dropchipEl, true);
    } else if (dropchipEl) {
        setBlockVisible(dropchipEl, false);
    } else if (hasPromotion && promotionValue !== null) {
        const slider = card.querySelector(".vehicle-card__slider .carousel-wrapper");
        if (slider) {
            const chip = document.createElement("span");
            chip.className = "vehicle-card__dropchip";
            chip.setAttribute("data-testid", "vehicle-card-dropchip");
            chip.textContent = `↓ −${formatNumber(promotionValue)} €`;
            slider.appendChild(chip);
        }
    }
    if (hasTotal && priceValueEl) {
        priceValueEl.textContent = `${formatTtcPrice(Number(totalPrice))} €`;
    }
    if (hasPromotion && hasPositivePrice(priceBeforePromotion) && priceWasEl) {
        priceWasEl.textContent = `${formatTtcPrice(Number(priceBeforePromotion))} €`;
        setBlockVisible(priceWasEl, true);
        totalBlock?.classList.add("vehicle-card__total--promo");
    } else if (priceWasEl) {
        setBlockVisible(priceWasEl, false);
        totalBlock?.classList.remove("vehicle-card__total--promo");
    } else if (
        hasPromotion &&
        hasPositivePrice(priceBeforePromotion) &&
        totalBlock
    ) {
        const was = document.createElement("span");
        was.className = "vehicle-card__price-was";
        was.setAttribute("data-testid", "vehicle-card-price-was");
        was.textContent = `${formatTtcPrice(Number(priceBeforePromotion))} €`;
        totalBlock.classList.add("vehicle-card__total--promo");
        totalBlock.insertBefore(was, priceValueEl);
    }
    setBlockVisible(totalBlock, hasTotal);

    const pricingBlock = card.querySelector(".vehicle-card__pricing");
    const pricingDivider = card.querySelector(
        ".vehicle-card__divider--pricing",
    );
    const showPricing = hasMonthly || hasTotal;
    setBlockVisible(pricingBlock, showPricing);
    setBlockVisible(pricingDivider, showPricing);

    const ctaLink = card.querySelector<HTMLAnchorElement>(".vehicle-card__cta");
    if (ctaLink) {
        ctaLink.href = vehicleUrl;
    }

    const slideLink = card.querySelector<HTMLAnchorElement>(".slide-link");
    if (slideLink) slideLink.href = vehicleUrl;

    const imageWidths = getVehicleCardImageWidthsForLayout(
        MARKETPLACE_CARD_IMAGE_LAYOUT,
    );
    const defaultImageWidth = getVehicleCardDefaultImageWidthForLayout(
        MARKETPLACE_CARD_IMAGE_LAYOUT,
    );
    const imageSizes = getVehicleCardImageSizesForLayout(
        MARKETPLACE_CARD_IMAGE_LAYOUT,
    );
    const vehicleForImages = vehicleImageContextFromHit(hit, photos);
    const useLocalPhotos =
        Array.isArray(hit.local_photos) && hit.local_photos.length > 0;

    card.dataset.imageLayout = MARKETPLACE_CARD_IMAGE_LAYOUT;
    card.dataset.localPhotos = useLocalPhotos ? "1" : "0";

    const eagerLoading = Boolean(hit.__lcpImage);
    const img = card.querySelector<HTMLImageElement>(".swiper-slide img");
    if (img && photo) {
        img.src = getVehicleImageUrl(vehicleForImages, photo, {
            width: defaultImageWidth,
        });
        img.srcset = getVehicleImageSrcSet(
            vehicleForImages,
            photo,
            imageWidths,
        );
        img.sizes = imageSizes;
        img.width = defaultImageWidth;
        img.height = Math.round((defaultImageWidth * 11) / 16);
        img.alt = generateVehicleImageAlt(vehicleForImages, 0) || altText;
        if (eagerLoading) {
            img.loading = "eager";
            img.fetchPriority = "high";
        } else {
            img.loading = "lazy";
            img.removeAttribute("fetchpriority");
        }
    } else if (img && !photo) {
        const placeholder = document.createElement("div");
        placeholder.className = "vehicle-card__image-placeholder";
        img.replaceWith(placeholder);
    }

    const nav = card.querySelector(".navigation");
    const counterEl = card.querySelector(".vehicle-card__photo-counter");
    const overlayEl = card.querySelector(".vehicle-card__overlay");
    const photosDiv = card.querySelector<HTMLElement>(".vehicle-card__photos");

    if (photos.length <= 1) {
        nav?.remove();
        counterEl?.remove();
        overlayEl?.remove();
        photosDiv?.remove();
    } else {
        const counterTotal = card.querySelector(
            ".vehicle-card__photo-counter__total",
        );
        if (counterTotal)
            counterTotal.textContent = String(
                hit.photos_count ?? photos.length,
            );

        const { displayPhotos } = getDisplayPhotos(photos, 5);
        if (photosDiv) {
            // Client-rendered cards: ImageKit URLs (same widths as Blade SSR).
            appendPhotoSourceSpans(photosDiv, card, displayPhotos);
            // Keep the deferred flag so setupVehicleCardCarousel builds the
            // carousel lazily (IntersectionObserver), like the SSR grid —
            // eagerly building 48 carousels per render is a main-thread
            // long task that shows up as INP input delay.
            photosDiv.dataset.deferred = "true";
            delete card.dataset.photoKeys;
        }

        const overlayLink = overlayEl?.querySelector<HTMLAnchorElement>(
            ".vehicle-card__overlay__cta",
        );
        if (overlayLink) overlayLink.href = vehicleUrl;
    }

    const favoriteBtn = card.querySelector<HTMLButtonElement>(
        ".vehicle-card__compare-favorite",
    );
    if (favoriteBtn && id) {
        favoriteBtn.dataset.vehicleId = String(id);
    }

    const wrapper = document.createElement("div");
    wrapper.appendChild(card);
    return wrapper.innerHTML;
}

/**
 * Initialises carousel navigation on each vehicle-card after InstantSearch
 * renders hits. Should be called from `search.on("render", ...)`.
 */
export function initVehicleCardCarousels(): void {
    if (typeof window === "undefined") return;
    setupAllVehicleCardCarousels();
    bindCompareFavoriteButtons();
}

let carouselInitScheduled = false;

/**
 * Idle-scheduled, coalesced initVehicleCardCarousels. Keeps carousel setup
 * out of the InstantSearch render task (and out of the tap-to-paint window).
 */
export function scheduleVehicleCardCarouselInit(): void {
    if (typeof window === "undefined" || carouselInitScheduled) return;
    carouselInitScheduled = true;
    const run = (): void => {
        carouselInitScheduled = false;
        initVehicleCardCarousels();
    };
    if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(run, { timeout: 1000 });
    } else {
        window.setTimeout(run, 1);
    }
}
